import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Segmented, Skeleton, Space, Tag, Typography, message,
} from 'antd';
import { ArrowLeftOutlined, CloudSyncOutlined, WifiOutlined } from '@ant-design/icons';
import type { AttendanceStatus, MarkAttendanceRequest, RegisterEntryRow } from '@velora/contracts';
import { outboxEnqueue } from '@velora/sync';
import { ApiError, productApi } from '../../api';

/**
 * Teacher — Attendance phone/desk.
 * Mobile-first: everyone defaults to present, one tap per exception.
 * O-02: when offline (or a POST fails), the mark payload queues in the outbox
 * and can be drained from the Offline Queue page.
 */
const STATUSES: AttendanceStatus[] = ['present', 'absent', 'late', 'sick', 'leave'];
const STATUS_TONE: Record<AttendanceStatus, string> = {
  present: 'success', absent: 'error', late: 'warning', sick: 'volcano', leave: 'default',
};

export function TeacherAttendancePage() {
  const {
    institution = '', grade = '', section = '',
  } = useParams<{ institution: string; grade: string; section: string }>();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const q = useQuery({
    queryKey: ['register', institution, today, grade, section],
    queryFn: () => productApi.getRegister(institution, today, grade, section),
    enabled: !!institution && !!grade && !!section,
  });

  const [draft, setDraft] = useState<Map<string, { status: AttendanceStatus; note?: string }>>(new Map());
  const [online, setOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    if (!q.data) return;
    // Seed draft with server state (defaulting empty rows to present, per U-14).
    const m = new Map<string, { status: AttendanceStatus; note?: string }>();
    for (const r of q.data.entries) {
      m.set(r.student_id, { status: r.status ?? 'present', note: r.note ?? undefined });
    }
    setDraft(m);
  }, [q.data?.session_id, q.data?.entries.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const counts = useMemo(() => {
    const c: Record<AttendanceStatus, number> = { present: 0, absent: 0, late: 0, sick: 0, leave: 0 };
    for (const v of draft.values()) c[v.status] += 1;
    return c;
  }, [draft]);

  const save = useMutation({
    mutationFn: (submit: boolean) => {
      const payload: MarkAttendanceRequest = {
        date: today, grade, section, submit,
        entries: Array.from(draft.entries()).map(([student_id, v]) => ({
          student_id, status: v.status, note: v.note,
        })),
      };
      if (!navigator.onLine) {
        outboxEnqueue('attendance.mark', { institution, payload });
        return Promise.resolve({ session_id: 'queued', state: 'open' as const, updated: payload.entries.length, queued: true });
      }
      return productApi.markAttendance(institution, payload).catch((err) => {
        // Network failure → queue and pretend it went out.
        if (err instanceof TypeError) {
          outboxEnqueue('attendance.mark', { institution, payload });
          return { session_id: 'queued', state: 'open' as const, updated: payload.entries.length, queued: true };
        }
        throw err;
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['register', institution, today, grade, section] });
      void qc.invalidateQueries({ queryKey: ['attendance-today', institution, today] });
      void message.success((r as { queued?: boolean }).queued ? 'Queued — will sync when online' : 'Saved');
    },
    onError: (e) => {
      const b = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      void message.error(b?.message ?? (e instanceof Error ? e.message : 'Failed'));
    },
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;

  const set = (student_id: string, status: AttendanceStatus) => {
    const next = new Map(draft);
    const cur = next.get(student_id) ?? { status: 'present' as AttendanceStatus };
    next.set(student_id, { ...cur, status });
    setDraft(next);
  };

  const submitted = q.data.state === 'submitted';

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Link to={`/i/${institution}/teacher`}><Button icon={<ArrowLeftOutlined />} type="text" size="small">Back</Button></Link>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {grade}{section ? `-${section}` : ''} · Attendance
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{today}</span>
            {' · '}
            {online ? <><WifiOutlined /> online</> : <span style={{ color: '#a24040' }}>offline · marks queue</span>}
          </Typography.Text>
        </div>
        {submitted && <Tag color="success">Submitted</Tag>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, fontSize: 11 }}>
        {STATUSES.map((s) => (
          <div key={s} style={{ textAlign: 'center', background: '#f7f7f5', padding: '4px 0', borderRadius: 4 }}>
            <div style={{ color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, fontSize: 10 }}>{s}</div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{counts[s]}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {q.data.entries.map((r) => (
          <StudentRow
            key={r.student_id}
            r={r}
            value={draft.get(r.student_id)?.status ?? 'present'}
            onChange={(s) => set(r.student_id, s)}
            disabled={submitted}
          />
        ))}
      </div>

      {submitted ? (
        <Alert type="info" showIcon message="Register submitted — re-open from admin if this needs a change (audit-logged)." />
      ) : (
        <Space wrap>
          <Button onClick={() => save.mutate(false)} loading={save.isPending}>
            Save draft
          </Button>
          <Button type="primary" icon={<CloudSyncOutlined />} loading={save.isPending} onClick={() => save.mutate(true)}>
            Submit register
          </Button>
        </Space>
      )}
    </Space>
  );
}

function StudentRow({
  r, value, onChange, disabled,
}: { r: RegisterEntryRow; value: AttendanceStatus; onChange: (s: AttendanceStatus) => void; disabled: boolean }) {
  return (
    <Card size="small" bodyStyle={{ padding: 10 }} style={{ minHeight: 56 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#eaf3fb', color: '#2f6ab0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
        }}>{r.name.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
          <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.admission_number}</div>
        </div>
        <Segmented
          size="small"
          value={value}
          disabled={disabled}
          onChange={(v) => onChange(v as AttendanceStatus)}
          options={STATUSES.map((s) => ({
            label: <span style={{ fontSize: 11 }}>{s.charAt(0).toUpperCase()}</span>,
            value: s,
            title: s,
          }))}
        />
        <Tag color={STATUS_TONE[value]} style={{ margin: 0, textTransform: 'capitalize', minWidth: 62, textAlign: 'center' }}>
          {value}
        </Tag>
      </div>
    </Card>
  );
}
